import React, { useRef } from 'react';
import PropTypes, { InferProps } from 'prop-types';
import ImageComponent from '@fuww/library/src/Image';
import ImagePreloader from './ImagePreloader';

const imagePropertyTypes = {
  alt: PropTypes.string.isRequired,
  cacheKey: PropTypes.string,
  className: PropTypes.string,
  finalFetchpriority: PropTypes.string,
  height: PropTypes.string,
  imageClassName: PropTypes.string,
  loadStrategy: PropTypes.string,
  maxHeight: PropTypes.string,
  objectFit: PropTypes.string,
  placeholder: PropTypes.string.isRequired,
  placeholderFetchpriority: PropTypes.string,
  placeholderLoadStrategy: PropTypes.string,
  preload: PropTypes.bool,
  preloadMediaQuery: PropTypes.string,
  showOnlyOn: PropTypes.string,
  sizes: PropTypes.string,
  src: PropTypes.string.isRequired,
  srcSet: PropTypes.string,
  width: PropTypes.string,
  padding: PropTypes.string,
  disableRoundedCorners: PropTypes.bool,
};

type ImagePropertyTypes = InferProps<typeof imagePropertyTypes>;

const Image = ({
  alt,
  cacheKey,
  className,
  finalFetchpriority,
  height,
  imageClassName,
  loadStrategy,
  maxHeight,
  objectFit,
  placeholder,
  placeholderFetchpriority,
  placeholderLoadStrategy,
  preload,
  preloadMediaQuery,
  showOnlyOn,
  sizes,
  src,
  srcSet,
  width,
  padding,
  disableRoundedCorners,
  ...properties
}: ImagePropertyTypes) => {
  const reference: React.RefObject<HTMLImageElement> = useRef(null);
  const loadStategyWithDefault = loadStrategy ?? 'on-visible';
  const placeholderLoadStrategyWithDefault = placeholderLoadStrategy
    ?? loadStategyWithDefault;

  const image = (
    <progressive-img
      alt={alt}
      class={className ?? undefined}
      final-fetchpriority={finalFetchpriority ?? 'low'}
      placeholder-fetchpriority={placeholderFetchpriority ?? 'low'}
      placeholder={placeholder}
      ref={reference}
      sizes={sizes ?? undefined}
      src={src}
      srcset={srcSet ?? undefined}
      title={alt}
      {... loadStategyWithDefault === 'on-visible' ? {} : {
        'load-strategy': loadStategyWithDefault,
      }}
      {...placeholderLoadStrategyWithDefault === 'on-visible' ? {} : {
        'placeholder-load-strategy': placeholderLoadStrategyWithDefault,
      }}
      {...properties}
    />
  );

  return (
    <>
      {preload && (
        <ImagePreloader
          src={src}
          placeholder={placeholder}
          sizes={sizes}
          srcSet={srcSet}
          preloadMediaQuery={preloadMediaQuery}
          placeholderFetchpriority={placeholderFetchpriority}
          finalFetchpriority={finalFetchpriority}
        />
      )}
      <ImageComponent
        alt={alt}
        className={imageClassName ?? undefined}
        height={height ?? 'auto'}
        image={image}
        maxHeight={maxHeight ?? 'none'}
        objectFit={objectFit ?? 'contain'}
        showOnlyOn={showOnlyOn ?? undefined}
        sizes={sizes ?? undefined}
        src={src}
        srcSet={srcSet ?? undefined}
        width={width ?? 'auto'}
        padding={padding ?? undefined}
        disableRoundedCorners={disableRoundedCorners ?? undefined}
      />
    </>
  );
};

Image.propTypes = imagePropertyTypes;

export default Image;
